import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useSearchParams } from 'react-router';
import ICSWidget from 'components/IAP/planningView/widgets/ICSWidget';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { getIncidentCardTitle } from '../../USCGPage';
import MaterialList from './MaterialList';
import PratusSelect from 'components/DesignSystems/New/PratusSelect';
import {
  HAZMAT_CLASSIFICATION_OPTIONS,
  INCIDENT_NEED_OPTIONS,
  POTENTIAL_HAZARD_OPTIONS,
  REQUIRED_PPE_OPTIONS,
} from 'components/IAP/ICSConstants';
import {
  HAZMATClassification,
  HazmatOption,
  IncidentNeed,
  IncidentNeedOption,
  PotentialHazard,
  PotentialHazardOption,
  RequiredPPE,
  RequiredPPEOption,
} from 'components/IAP/initialResponsePage/SafetyAnalysis/types';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import {
  Material,
  useGetHazmats,
  useGetIncidentInfo,
  usePostHazmats,
  useUpdateIncidentInfo,
} from 'components/IAP/hooks/useSafetyAnalysis';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { toast } from 'react-toastify';

export type HazardousMaterialsFormState = {
  hazmatClassification: HAZMATClassification[];
  material: Material[];
  potentialHazards: PotentialHazard[];
  incidentNeeds: IncidentNeed[];
  additionalProductInfo: string;
  requiredPPE: RequiredPPE[];
  notesOnPPE: string;
  lelInstruments: string;
  o2Instruments: string;
  toxicityInstruments: string;
  radiologicalInstruments: string;
  hazardMonitoringComments: string;
  standardOperatingProcedures: string;
  decontaminationProcedures: string;
  medicalMonitoringRequired: boolean;
  emergencyProcedures: string;
  medicalTreatmentAndTransportInPlace: boolean;
  otherClassification: null; // deprecated
  airMonitoringRequired: null; // deprecated
};

const HazardousMaterials: React.FC = () => {
  const { data: hazmatData } = useGetHazmats();

  const { data: incidentInfoData } = useGetIncidentInfo();

  if (!hazmatData || !incidentInfoData) {
    return <div className="d-flex justify-content-center">Loading...</div>;
  }

  const initialData = {
    hazmatClassification: hazmatData.classifications ?? [],
    material: hazmatData.material ?? ([] as Material[]),
    potentialHazards: incidentInfoData?.potentialHazards ?? [],
    incidentNeeds: hazmatData.incidentNeeds ?? [],
    additionalProductInfo: hazmatData.additionalProductInfo ?? '',
    requiredPPE: incidentInfoData?.requiredPpe ?? ([] as RequiredPPE[]),
    notesOnPPE: incidentInfoData?.notesOnPpe ?? '',
    lelInstruments: hazmatData.lelInstruments ?? '',
    o2Instruments: hazmatData.o2Instruments ?? '',
    toxicityInstruments: hazmatData.toxicityInstruments ?? '',
    radiologicalInstruments: hazmatData.radiologicalInstruments ?? '',
    hazardMonitoringComments: hazmatData.hazardMonitoringComments ?? '',
    standardOperatingProcedures: hazmatData.sops ?? '',
    decontaminationProcedures: hazmatData.decontaminationProcedure ?? '',
    medicalMonitoringRequired: hazmatData.medicalMonitoring ?? false,
    medicalTreatmentAndTransportInPlace: hazmatData.medicalTreatment ?? false,
    emergencyProcedures: hazmatData.emergencyProcedures ?? '',
    otherClassification: null, // deprecated
    airMonitoringRequired: null, // deprecated
  };

  return <HazardousMaterialsForm initialData={initialData} />;
};

type HazardousMaterialsFormProps = {
  initialData: HazardousMaterialsFormState;
};

const HazardousMaterialsForm: React.FC<HazardousMaterialsFormProps> = ({
  initialData,
}) => {
  const { exportAsForm } = useIcsFormPdfPreview();
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-hazardous-materials`;

  const [formState, setFormState] = useState(initialData);

  const postHazmats = usePostHazmats(formState);
  const updateIncidentInfo = useUpdateIncidentInfo(formState);

  const handleSave = async () => {
    await postHazmats.mutateAsync();
    await updateIncidentInfo.mutateAsync();

    toast.success('Hazardous Materials information saved successfully!', {});
  };

  const handleExport = async () => {
    await postHazmats.mutateAsync();
    await updateIncidentInfo.mutateAsync();

    exportAsForm({
      formname: 'ICS_208HM_CG',
      data: {},
      mapSketch: null,
    });
  };

  return (
    <>
      <ConditionalCollapsibleCard
        sectionKey={sectionKey}
        className="planning-widget"
        contentClassName="content mt-2"
        headerLeft={
          <ICSWidget.HeaderLeft
            iconName="checklist"
            iconColor="primary"
            title={getIncidentCardTitle('hazardousMaterials')}
            subtitle="Incident Hazardous Material Assessment"
          />
        }
        headerRight={
          <ICSWidget.HeaderRight
            icsRoles={['PSC']}
            handleExport={handleExport}
            exportText="Preview ICS-208HM"
          />
        }
      >
        <div className="mb-4" style={{ width: '50%' }}>
          <PratusSelect
            label="HAZMAT Classification"
            placeholder="Click to select hazardous materials..."
            options={HAZMAT_CLASSIFICATION_OPTIONS}
            value={HAZMAT_CLASSIFICATION_OPTIONS.filter((option) =>
              formState.hazmatClassification?.includes(option.value)
            )}
            onChange={(value) => {
              const newValues =
                (value as HazmatOption[] | null)?.map((v) => v.value) ?? [];
              setFormState((prev) => ({
                ...prev,
                hazmatClassification: newValues,
              }));
            }}
            isMulti
          />
        </div>
        <div className="mb-4">
          <MaterialList
            material={formState.material}
            updateMaterials={(material) => {
              setFormState((prev) => ({ ...prev, material }));
            }}
          />
        </div>

        <div className="mb-4">
          <label className="form-label">Additional Product Information</label>
          <StylishNewTextArea
            placeholder="Enter a description..."
            value={formState.additionalProductInfo}
            onChange={(e) => {
              setFormState((prev) => ({
                ...prev,
                additionalProductInfo: e.target.value,
              }));
            }}
            rows={5}
          />
        </div>

        <div className="mb-4" style={{ width: '50%' }}>
          <PratusSelect
            label="Potential Physical Effects"
            placeholder="Click to select potential hazards..."
            options={POTENTIAL_HAZARD_OPTIONS}
            value={POTENTIAL_HAZARD_OPTIONS.filter((option) =>
              formState.potentialHazards?.includes(option.value)
            )}
            onChange={(value) => {
              const newValues =
                (value as PotentialHazardOption[] | null)?.map(
                  (v) => v.value
                ) ?? [];
              setFormState((prev) => ({
                ...prev,
                potentialHazards: newValues,
              }));
            }}
            isMulti
          />
        </div>
        <div className="mb-4" style={{ width: '50%' }}>
          <PratusSelect
            label="Does the incident require a need for any of the following?"
            placeholder="Click to select required information..."
            options={INCIDENT_NEED_OPTIONS}
            value={INCIDENT_NEED_OPTIONS.filter((option) =>
              formState.incidentNeeds?.includes(option.value)
            )}
            onChange={(value) => {
              const newValues =
                (value as IncidentNeedOption[] | null)?.map((v) => v.value) ??
                [];
              setFormState((prev) => ({
                ...prev,
                incidentNeeds: newValues,
              }));
            }}
            isMulti
          />
        </div>
        <div className="mb-4" style={{ width: '50%' }}>
          <PratusSelect
            label="Required Personal Protective Equipment (PPE)"
            placeholder="Click to select PPE..."
            options={REQUIRED_PPE_OPTIONS}
            value={REQUIRED_PPE_OPTIONS.filter((option) =>
              formState.requiredPPE?.includes(option.value)
            )}
            onChange={(value) => {
              const newValues =
                (value as RequiredPPEOption[] | null)?.map((v) => v.value) ??
                [];
              setFormState((prev) => ({ ...prev, requiredPPE: newValues }));
            }}
            isMulti
          />
        </div>
        <div className="mb-4">
          <label className="form-label">Comments on PPE</label>
          <StylishNewTextArea
            placeholder="Enter comments..."
            value={formState.notesOnPPE}
            onChange={(e) => {
              setFormState((prev) => ({
                ...prev,
                notesOnPPE: e.target.value,
              }));
            }}
            rows={5}
          />
        </div>
        <div className="mb-4">
          <div className="mb-2">
            <strong>Hazard Monitoring</strong>
          </div>
          <div
            className="d-grid gap-3"
            style={{
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              alignItems: 'start',
            }}
          >
            <div>
              <label className="form-label">LEL Instrument(s)</label>
              <StylishNewInput
                placeholder="Enter instrument notes..."
                value={formState.lelInstruments}
                onChange={(e) => {
                  setFormState((prev) => ({
                    ...prev,
                    lelInstruments: e.target.value,
                  }));
                }}
              />
            </div>
            <div>
              <label className="form-label">O2 Instrument(s)</label>
              <StylishNewInput
                placeholder="Enter instrument notes..."
                value={formState.o2Instruments}
                onChange={(e) => {
                  setFormState((prev) => ({
                    ...prev,
                    o2Instruments: e.target.value,
                  }));
                }}
              />
            </div>
            <div>
              <label className="form-label">Toxicity/PPM Instrument(s)</label>
              <StylishNewInput
                placeholder="Enter instrument notes..."
                value={formState.toxicityInstruments}
                onChange={(e) => {
                  setFormState((prev) => ({
                    ...prev,
                    toxicityInstruments: e.target.value,
                  }));
                }}
              />
            </div>
            <div>
              <label className="form-label">Radiological Instrument(s)</label>
              <StylishNewInput
                placeholder="Enter instrument notes..."
                value={formState.radiologicalInstruments}
                onChange={(e) => {
                  setFormState((prev) => ({
                    ...prev,
                    radiologicalInstruments: e.target.value,
                  }));
                }}
              />
            </div>
          </div>
        </div>
        <div className="mb-4">
          <label className="form-label">Comments</label>
          <StylishNewTextArea
            placeholder="Enter comments..."
            value={formState.hazardMonitoringComments}
            onChange={(e) => {
              setFormState((prev) => ({
                ...prev,
                hazardMonitoringComments: e.target.value,
              }));
            }}
            rows={5}
          />
        </div>
        <div className="mb-4">
          <label className="form-label">
            Standard Operating Procedures (SOP) and Safe Work Practices
          </label>
          <StylishNewTextArea
            placeholder="Enter comments..."
            value={formState.standardOperatingProcedures}
            onChange={(e) => {
              setFormState((prev) => ({
                ...prev,
                standardOperatingProcedures: e.target.value,
              }));
            }}
            rows={5}
          />
        </div>
        <div className="mb-4">
          <label className="form-label">Decontamination Procedures</label>
          <StylishNewTextArea
            placeholder="Enter a procedures..."
            value={formState.decontaminationProcedures}
            onChange={(e) => {
              setFormState((prev) => ({
                ...prev,
                decontaminationProcedures: e.target.value,
              }));
            }}
            rows={5}
          />
        </div>
        <div className="d-flex flex-row align-items-left gap-4 medical-row mb-4">
          <>Medical</>
          <StylishNewCheckbox
            label="Medical Monitoring Required?"
            checked={formState.medicalMonitoringRequired}
            onClick={() =>
              setFormState((prev) => ({
                ...prev,
                medicalMonitoringRequired: !prev.medicalMonitoringRequired,
              }))
            }
          />
          <StylishNewCheckbox
            label="Medical Treatment and Transport in place?"
            checked={formState.medicalTreatmentAndTransportInPlace}
            onClick={() =>
              setFormState((prev) => ({
                ...prev,
                medicalTreatmentAndTransportInPlace:
                  !prev.medicalTreatmentAndTransportInPlace,
              }))
            }
          />
        </div>
        <label className="form-label">Emergency Procedures</label>
        <StylishNewTextArea
          placeholder="Enter a procedures..."
          value={formState.emergencyProcedures}
          onChange={(e) => {
            setFormState((prev) => ({
              ...prev,
              emergencyProcedures: e.target.value,
            }));
          }}
          rows={5}
        />
        <div className="mt-3 d-flex justify-content-end">
          <StylishNewButton onClick={handleSave}>
            Save Hazardous Materials
          </StylishNewButton>
        </div>
      </ConditionalCollapsibleCard>
    </>
  );
};

export default HazardousMaterials;
